import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, aboutInfo } from '@/mocks/movies';

export default function About() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: '首页',
                      item: 'https://www.yayacinema.com/',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: '关于我们',
                    },
                  ],
                }),
              }}
            />

            <section className="mb-12 md:mb-16">
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                  关于丫丫影院
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                  {siteInfo.tagline}
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-6 md:p-10 mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {aboutInfo.brandStory}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  <strong className="text-foreground-900">我们的使命：</strong>{aboutInfo.mission}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                {aboutInfo.values.map((v, idx) => (
                  <div key={idx} className="bg-background-50 rounded-lg p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                      <i className={`text-primary-600 text-xl ${
                        idx === 0 ? 'ri-heart-line' :
                        idx === 1 ? 'ri-stack-line' :
                        idx === 2 ? 'ri-cpu-line' :
                        'ri-gift-line'
                      }`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{v.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12 md:mb-16">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500">
                  来自百万用户的好评
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {aboutInfo.testimonials.map((t, idx) => (
                  <div key={idx} className="bg-background-50 rounded-lg p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${
                            i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-xs">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-400">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12 md:mb-16" id="contact">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  如有任何疑问，欢迎随时联系
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                    <i className="ri-mail-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.email}</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                    <i className="ri-wechat-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信</h3>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.wechat}</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                    <i className="ri-time-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.workingHours}</p>
                </div>
              </div>
            </section>

            <section className="mb-12 md:mb-16">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">
                  快速解答您的疑问
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {aboutInfo.faqs.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: '关于丫丫影院',
              description: `了解${siteInfo.name}的品牌故事、用户口碑和联系方式。${siteInfo.description}`,
              about: {
                '@type': 'Organization',
                name: siteInfo.name,
                description: siteInfo.description,
                email: siteInfo.email,
                foundingDate: '2018',
              },
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: aboutInfo.faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.a,
                },
              })),
            }),
          }}
        />
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}